import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { TouchableHighlight, View } from 'react-native';
import { CardView } from 'design-system/CardView';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconArrow from 'design-system/icons/IconArrow';
import Logo from 'design-system/Logo';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem, AppView, ButtonGroup, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectPositionsForTradingAccount, selectTradingAccountFromId } from '../reducers/selectors';
import useStockMeta from '../hooks/useStockMeta';
import { getTradingAccountName } from '../utils';
import { isWeb } from '../../../constants';
const strings = {
    title: 'Select a fund or ETF to invest',
    subtitle: 'Pick one of your current investments, or choose from the full fund list.',
    exploreMoreFunds: 'Explore more funds',
};
const PositionRow = ({ position, onPress, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const stockMeta = useStockMeta(position.assetId);
    const handlePress = useCallback(() => onPress(position.assetId), [onPress, position.assetId]);
    return (<TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={handlePress}>
      <View style={styles.row}>
        <Logo R44 uri={stockMeta?.iconUrl || position.iconUrl}/>
        <Spacer w={16}/>
        <View style={styles.name}>
          <Text Text-16>{stockMeta?.name || position.name}</Text>
        </View>
        <Spacer w={16}/>
        <IconArrow />
      </View>
    </TouchableHighlight>);
};
const SelectAssetFromPositions = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const positions = useAppSelector((state) => selectPositionsForTradingAccount(state, route.params?.tradingAccountId));
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const tradingAccount = useAppSelector((state) => selectTradingAccountFromId(state, route.params?.tradingAccountId));
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary]);
    const onSelectAsset = useCallback((assetId) => {
        navigation.navigate('InvestStock', {
            assetId,
            depositAndBuyFlowParams: {
                amount: route.params.amount,
                depositMethod: route.params.depositMethod,
                tradingAccountId: route.params.tradingAccountId,
            },
        });
    }, [route.params.amount, route.params.depositMethod, route.params.tradingAccountId]);
    const onExploreMoreFunds = useCallback(() => {
        navigation.navigate('InvestDiscover', {
            start: 'doItYourself',
            depositAndBuyFlowParams: {
                amount: route.params.amount,
                depositMethod: route.params.depositMethod,
                tradingAccountId: route.params.tradingAccountId,
            },
        });
    }, [route.params.amount, route.params.depositMethod, route.params.tradingAccountId]);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title={strings.title} subTitle={strings.subtitle} preTitle={getTradingAccountName(tradingAccount)}/>
          <CardView noMarginTop>
            {positions.map((position) => (<PositionRow key={position.assetId} position={position} onPress={onSelectAsset}/>))}
          </CardView>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brandReversed title={strings.exploreMoreFunds} onPress={onExploreMoreFunds}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    row: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    name: {
        flex: 1,
    },
}));
export default SelectAssetFromPositions;
